/*
 * PrdInlineReviewCard — Slice 12 v2.
 *
 * Renders the freshly-drafted PRD inline so the user reviews it in
 * Orbit rather than having to jump to validx workspace to read it.
 * The PrdDocument arrives via the PRD_DRAFT_READY webhook payload
 * (markdown + structured sections), stashed in
 * engagement_request.routing_decision.prd_document and surfaced via
 * /home.
 *
 * v1 surface:
 *   - Eyebrow + PRD title
 *   - 9 collapsible sections (Executive Summary → Acceptance Criteria)
 *   - Each section: heading + summary preview + click-to-expand body
 *   - "Edit section" button per section → pushes a synthetic Orbit
 *     turn that opens the conversation focused on that section. The
 *     actual edit happens in the workspace later; this is the discuss-
 *     first affordance.
 *   - Footer: [Looks right — start building] / [Talk to a partner first]
 *     / [Add more context first]
 *   - "Start building" calls /api/intake/approve-prd → opens workspace
 *
 * Brand: light card surface (#eef3ff), 10px radius, no card border,
 * Plex Serif italic for the PRD title (editorial moment), Plex Mono
 * caps eyebrow, compact section accordion. Black primary CTA with the
 * canonical brand hover lift + blue glow shadow.
 */

import React, { useMemo, useState } from 'react';
import { ArrowRight, ChevronDown, LayoutGrid } from 'lucide-react';

import type {
  PrdInlineReviewPayload,
  PrdSectionContent,
} from '../../lib/dossier-types';


// Canonical section ordering + display labels. Keys match validx's
// markdownToPrdSections mapping (Executive Summary → executiveSummary
// etc.). We keep both display name and short eyebrow tag so each row
// gets the same numbered eyebrow treatment as other directive cards.
const SECTION_ORDER: Array<{ key: string; label: string }> = [
  { key: 'executiveSummary',       label: 'Executive summary' },
  { key: 'problemStatement',       label: 'Problem statement' },
  { key: 'goalsAndSuccessCriteria', label: 'Goals and success criteria' },
  { key: 'requirements',           label: 'Requirements' },
  { key: 'technicalArchitecture',  label: 'Technical architecture' },
  { key: 'integrations',           label: 'Integrations' },
  { key: 'implementationPlan',     label: 'Implementation plan' },
  { key: 'risksAndMitigations',    label: 'Risks and mitigations' },
  { key: 'acceptanceCriteria',     label: 'Acceptance criteria' },
];


export interface PrdInlineReviewCardProps {
  payload: PrdInlineReviewPayload;
  clientId?: string;
  /** "See it on workspace" — parent opens the validx workspace (PlanView)
   *  for the given project id. */
  onSeeOnWorkspace: (validxProjectId: string | null) => void;
  /** "Go to dashboard" — parent navigates to the customer dashboard. */
  onGoToDashboard: () => void;
  /** Hide the "Go to dashboard" button. True on the dashboard side panel —
   *  you're already on the dashboard, so the button is only meaningful on the
   *  full-page /orbit/intake screen. */
  hideDashboard?: boolean;
  disabled?: boolean;
}


const str = (x: unknown): string =>
  typeof x === 'string' && x.trim() ? x.trim() : '';

// Render ONE array item (goal, requirement, acceptance criterion, tech-stack
// entry…) into a readable line. PRD item shapes vary wildly — some carry a
// `title`, some only a `description`, some a `category`/`technology` pair —
// so we resolve a headline from the first present label-ish field, then a
// supporting metric/detail that ISN'T the headline. When nothing matches a
// known field, fall back to joining the remaining scalar values (skipping
// `id`/`*Ids` references) so no real content is silently dropped — the bug
// where goals[]/criteria[] (description-only) collapsed to an empty body.
function stringifyItem(item: unknown): string {
  if (typeof item === 'string') return item.trim();
  if (!item || typeof item !== 'object') return '';
  const o = item as Record<string, unknown>;

  const headline =
    str(o.title) || str(o.text) || str(o.name) || str(o.label)
    || str(o.category) || str(o.description);
  if (headline) {
    const usedDescription = headline === str(o.description);
    const detail =
      str(o.successMetric) || str(o.metric) || str(o.target)
      || str(o.technology) || (usedDescription ? '' : str(o.description))
      || str(o.detail) || str(o.rationale);
    return detail ? `${headline} — ${detail}` : headline;
  }

  // No recognised headline field — surface whatever scalar content exists.
  const parts: string[] = [];
  for (const [k, v] of Object.entries(o)) {
    if (k === 'id' || /ids?$/i.test(k)) continue;  // skip refs (requirementIds…)
    if (typeof v === 'string' && v.trim()) parts.push(v.trim());
  }
  return parts.join(' — ');
}


function sectionPreview(content: PrdSectionContent | undefined): string {
  if (!content) return '';
  // Try `content` first (Executive Summary uses this), then fall back
  // to a flat join of all string-valued sub-keys (e.g. problemStatement
  // .currentSituation + .keyMetrics + .opportunity).
  if (typeof content.content === 'string' && content.content.trim()) {
    return content.content.trim();
  }
  const pieces: string[] = [];
  for (const v of Object.values(content)) {
    if (typeof v === 'string' && v.trim()) {
      pieces.push(v.trim());
    } else if (Array.isArray(v)) {
      for (const item of v) {
        const line = stringifyItem(item);
        if (line) pieces.push(line);
      }
    }
  }
  return pieces.join(' · ');
}


function sectionBody(content: PrdSectionContent | undefined): string {
  if (!content) return '';
  // Expanded form — render the full set of fields the LLM filled,
  // joined with section breaks for readability.
  if (typeof content.content === 'string' && content.content.trim()) {
    return content.content.trim();
  }
  const lines: string[] = [];
  for (const [k, v] of Object.entries(content)) {
    if (typeof v === 'string' && v.trim()) {
      const label = k.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase());
      lines.push(`${label}: ${v.trim()}`);
    } else if (Array.isArray(v)) {
      const label = k.replace(/([A-Z])/g, ' $1').replace(/^./, c => c.toUpperCase());
      const items = v.map(stringifyItem).filter(Boolean);
      if (items.length) lines.push(`${label}:\n${items.join('\n')}`);
    }
  }
  return lines.join('\n\n');
}


export const PrdInlineReviewCard: React.FC<PrdInlineReviewCardProps> = ({
  payload,
  onSeeOnWorkspace,
  onGoToDashboard,
  hideDashboard,
  disabled,
}) => {
  const sections = payload.document.sections || {};
  const projectId = payload.document.validx_project_id ?? null;
  const [expanded, setExpanded] = useState<string | null>(SECTION_ORDER[0].key);

  // Filter to sections that actually have content. Empty/skipped
  // sections (e.g. when LLM left risksAndMitigations blank) drop out.
  const populated = useMemo(
    () => SECTION_ORDER.filter(s => sectionPreview(sections[s.key]).length > 0),
    [sections],
  );

  return (
    <div className="aa-prd-review">
      <header className="aa-prd-review-header">
        <span className="aa-prd-review-eyebrow">
          PRD ready · {populated.length} section{populated.length !== 1 ? 's' : ''}
        </span>
        <h3 className="aa-prd-review-title">{payload.title}</h3>
      </header>

      <div className="aa-prd-review-sections">
        {populated.map((s, idx) => {
          const isOpen = expanded === s.key;
          const preview = sectionPreview(sections[s.key]);
          const body = sectionBody(sections[s.key]);
          const previewSnippet = preview.length > 140
            ? `${preview.slice(0, 137).trim()}…`
            : preview;
          return (
            <div
              key={s.key}
              className={
                'aa-prd-review-section'
                + (isOpen ? ' aa-prd-review-section--open' : '')
              }
            >
              <button
                type="button"
                className="aa-prd-review-section-head"
                onClick={() => setExpanded(isOpen ? null : s.key)}
                aria-expanded={isOpen}
              >
                <span className="aa-prd-review-section-index">
                  {String(idx + 1).padStart(2, '0')}
                </span>
                <span className="aa-prd-review-section-name">{s.label}</span>
                <ChevronDown
                  size={14}
                  strokeWidth={2.25}
                  className="aa-prd-review-section-chevron"
                />
              </button>
              {!isOpen && previewSnippet && (
                <p className="aa-prd-review-section-preview">{previewSnippet}</p>
              )}
              {isOpen && (
                <div className="aa-prd-review-section-body">
                  <div className="aa-prd-review-section-content">{body}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <footer className="aa-prd-review-footer">
        <button
          type="button"
          className="aa-prd-review-primary"
          onClick={() => onSeeOnWorkspace(projectId)}
          disabled={disabled}
        >
          <span>See it on workspace</span>
          <ArrowRight size={13} strokeWidth={2.5} />
        </button>
        {!hideDashboard && (
          <button
            type="button"
            className="aa-prd-review-secondary"
            onClick={onGoToDashboard}
            disabled={disabled}
          >
            <LayoutGrid size={12} strokeWidth={2.25} />
            <span>Go to dashboard</span>
          </button>
        )}
      </footer>
    </div>
  );
};

export default PrdInlineReviewCard;
